import React, { Dispatch, SetStateAction, forwardRef } from 'react';
import ColorWrapper from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import { View } from 'react-native';
import { ImageDocumentStoreItem, ImageHandlingDisplayMode } from '@v2/components/imageHandling/types/types';
import TouchableElement from '@components/common/touchableElement';
import { isWeb } from '@helpers';
import Svg from '@styles/icons/svg';
import { useManageHeaderIcons } from './hooks/useManageHeaderIcons';
import { ImageHandlingHeaderProps } from '@v2/components/imageHandling/components/Header/Header';
import { IconPositions, HeaderIconKey } from './types';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

const HEADER_ICON_TEST_IDS: Partial<Record<HeaderIconKey, string>> = {
  [HeaderIconKey.CROP]: TEST_IDS.documentsHeader.iconCrop,
  [HeaderIconKey.ROTATE]: TEST_IDS.documentsHeader.iconRotate,
  [HeaderIconKey.SHARE]: TEST_IDS.documentsHeader.iconShare,
  [HeaderIconKey.MORE]: TEST_IDS.documentsHeader.iconMore,
  [HeaderIconKey.EXPAND]: TEST_IDS.documentsHeader.iconExpand,
};

export interface HeaderIconsProps
  extends Pick<
    ImageHandlingHeaderProps,
    | 'rotateImage'
    | 'shouldShowAddImageModal'
    | 'imageContextForAdding'
    | 'C'
    | 'shouldHideDownloadButton'
    | 'shouldHideAttachButton'
    | 'shouldShowAddButtonIfSingleImage'
    | 'shouldHideThumbnailButton'
    | 'onClose'
    | 'onSuccessImageUpload'
    | 'iconsContainerStyle'
    | 'navigateBackFallBackScreen'
    | 'shouldDisplayModeIcon'
    | 'shouldHideIconsWithoutImg'
    | 'scale'
    | 'font'
    | 'setMode'
    | 'onExpandIconPress'
    | 'onCollapseIconPress'
  > {
  triggerCrop: () => void;
  triggerDraw?: () => void;
  isDrawing?: boolean;
  triggerInfoPopUp: () => void;
  triggerRotatePopUp: () => void;
  showInfoPopUp: boolean;
  showRotatePopUp: boolean;
  isSingleImage: boolean;
  showAttachModal: boolean;
  img: ImageDocumentStoreItem;
  toggleAttachModal: () => void;
  download: () => void;
  enabledIcons?: HeaderIconKey[];
}

export type HeaderIconsHandle = IconPositions;

const HeaderIcons = forwardRef<HeaderIconsHandle, HeaderIconsProps>((props, ref) => {
  const { iconsContainerStyle, enabledIcons, ...manageHeaderIconsParams } = props;

  const { scale = initialScale } = props;

  const { hoveredStyle, iconsConfig, registerIconRef } = useManageHeaderIcons({ ...manageHeaderIconsParams, enabledIcons, ref });

  return (
    <View style={[{ flexDirection: 'row', alignItems: 'center', gap: scale(18) }, iconsContainerStyle]}>
      {iconsConfig.map((c) => {
        if (!c.shouldDisplay) {
          return null;
        }

        return (
          <TouchableElement key={c.key} testID={HEADER_ICON_TEST_IDS[c.key]} onPress={c.onPress} disabled={c.disabled}>
            {({ boxStyle, hovered, pressed }) => (
              <View
                ref={(viewRef) => registerIconRef(c.trackPositionKey, viewRef)}
                style={[
                  boxStyle,
                  hoveredStyle({ hovered, pressed, isModalOpen: c.isModalOpened || false, disabled: c.disabled }),
                  c.containerStyles,
                  isWeb && ({ outline: 'none' } as any),
                  c.disabled && { opacity: 0.4 },
                ]}
              >
                <Svg icon={c.icon} width={c.width} height={c.height} color={c.color} />
                {c.elementToTheRightOfIcon && c.elementToTheRightOfIcon}
              </View>
            )}
          </TouchableElement>
        );
      })}
    </View>
  );
});

HeaderIcons.displayName = 'HeaderIcons';

export default HeaderIcons;
